import { MutableRefObject, useEffect, useState } from 'react';
import Link from 'next/link';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useRef } from 'react';
import useIsMounted from 'src/hooks/useIsMounted';
import classNames from 'classnames';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';

type Props = {
  totalCount: number;
  seeAllUrl?: string;
  numRecord: number;
  perSlide?: number;
  renderTitle: () => JSX.Element;
  infinite?: boolean;
  hasBlankCard?: boolean;
  scrollContainerClassName?: string;
  btnContainerClassName?: string;
  isArrowBottom?: boolean;
};

type ScrollData = {
  index: number;
  containerWidth: number;
  cardWidth: number;
  totalWidth: number;
  numOfScroll: number;
  sliding: boolean;
};

const ArrowButton: React.FC<{
  numOfCards: number;
  perSlide: number;
  btnContainerClassName?: string;
  infinite?: boolean;
  scrollValueRef: MutableRefObject<ScrollData | undefined>;
  containerRef: MutableRefObject<HTMLDivElement | null>;
}> = ({ numOfCards, perSlide, btnContainerClassName, scrollValueRef, containerRef, infinite }) => {
  const [disabledPrev, setDisabledPrev] = useState(!infinite);
  const [disabledNext, setDisabledNext] = useState(false);
  const move = () => {
    if (!scrollValueRef.current) return;
    if (!containerRef.current) return;
    let lastNumOfCards = numOfCards % perSlide;
    let prevIndex = scrollValueRef.current.index - 1;
    prevIndex = prevIndex < 0 ? 0 : prevIndex;
    const isScrollToEnd = scrollValueRef.current.index + 1 === scrollValueRef.current.numOfScroll && lastNumOfCards === 0
    let distance =
      scrollValueRef.current.index + 1 < scrollValueRef.current.numOfScroll || isScrollToEnd
        ? scrollValueRef.current.index * scrollValueRef.current.containerWidth
        : prevIndex * scrollValueRef.current.containerWidth +
          lastNumOfCards * scrollValueRef.current.cardWidth;
    containerRef.current.scroll({ left: distance, behavior: 'smooth' });
    const currentValues = scrollValueRef.current;
    setTimeout(() => {
      scrollValueRef.current = { ...currentValues, sliding: false };
    }, 200);
  };

  return (
    <div className={`tw-hidden md:tw-flex tw-items-center ${btnContainerClassName || ''}`}>
      <div className="tw-flex tw-items-center">
        <div
          className={classNames('tw-inline-block tw-text-center tw-mr-2', {
            'tw-cursor-pointer tw-text-brand-navy': !disabledPrev,
            'tw-text-grey-lighter': disabledPrev,
          })}
          onClick={() => {
            if (!scrollValueRef.current) return;
            if (scrollValueRef.current.sliding) return;
            let nextIndex = scrollValueRef.current.index - 1;
            if (nextIndex >= 0) {
              scrollValueRef.current = {
                ...scrollValueRef.current,
                index: nextIndex,
                sliding: true,
              };
              if (!infinite && nextIndex === 0) {
                setDisabledPrev(true);
              }
            } else if (infinite) {
              scrollValueRef.current = {
                ...scrollValueRef.current,
                index: scrollValueRef.current.numOfScroll - 1,
                sliding: true,
              };
            }

            if (!infinite) {
              setDisabledNext(false);
            }
            move();
          }}
        >
          <HiOutlineArrowNarrowLeft size={28} strokeWidth={2} />
        </div>
        <div className="tw-h-8 tw-border-l-2 tw-border-brand-navy tw-transform tw-rotate-45 tw-mx-4"></div>
        <div
          className={classNames('tw-inline-block tw-text-center', {
            'tw-cursor-pointer tw-text-brand-navy': !disabledNext,
            'tw-text-grey-lighter': disabledNext,
          })}
          onClick={() => {
            if (!scrollValueRef.current) return;
            if (scrollValueRef.current.sliding) return;
            let nextIndex = scrollValueRef.current.index + 1;
            if (nextIndex < scrollValueRef.current.numOfScroll) {
              scrollValueRef.current = {
                ...scrollValueRef.current,
                index: nextIndex,
                sliding: true,
              };

              if (!infinite && nextIndex + 1 === scrollValueRef.current.numOfScroll) {
                setDisabledNext(true);
              }
            } else if (infinite) {
              scrollValueRef.current = { ...scrollValueRef.current, index: 0, sliding: true };
            }

            if (!infinite) {
              setDisabledPrev(false);
            }
            move();
          }}
        >
          <HiOutlineArrowNarrowRight size={28} strokeWidth={2} />
        </div>
      </div>
    </div>
  );
};

const ScrollableSection: React.ReactFCWithChildren<Props> = ({
  children,
  totalCount,
  seeAllUrl,
  numRecord,
  perSlide = 3,
  renderTitle,
  infinite = true,
  hasBlankCard,
  scrollContainerClassName,
  btnContainerClassName,
  isArrowBottom,
}) => {
  const isMounted = useIsMounted();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const slideCount = Math.ceil(numRecord / perSlide);
  const scrollValueRef = useRef<ScrollData>();
  const numBlankCard = hasBlankCard ? 1 : 0;
  let numOfCards = numRecord;
  numOfCards = totalCount > numRecord ? numOfCards + numBlankCard : numOfCards;

  useEffect(() => {
    if (!isMounted) return;
    if (!containerRef.current) return;
    const card = containerRef.current.querySelector('.js-card-scrollable') as
      | HTMLElement
      | undefined;
    if (card && containerRef.current) {
      const containerWidth = containerRef.current.parentElement?.offsetWidth as number;
      scrollValueRef.current = {
        index: 0,
        cardWidth: card.offsetWidth,
        totalWidth: card.offsetWidth * numOfCards,
        containerWidth: containerWidth,
        numOfScroll: Math.ceil((card.offsetWidth * numOfCards) / containerWidth),
        sliding: false,
      };
    }
  }, [isMounted]);

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-8 tw-relative">
        <div className="tw-w-full md:tw-w-auto tw-flex-grow">{renderTitle()}</div>

        {slideCount > 1 && !isArrowBottom && (
          <ArrowButton
            btnContainerClassName={btnContainerClassName}
            containerRef={containerRef}
            scrollValueRef={scrollValueRef}
            numOfCards={numOfCards}
            perSlide={perSlide}
            infinite={infinite}
          />
        )}
      </div>

      <div
        ref={containerRef}
        className={`${
          scrollContainerClassName || 'tw-overflow-x-auto'
        } tw-overflow-y-hidden hide-scrollbar tw-transition-transform`}
      >
        {children}
      </div>
      {slideCount > 1 && isArrowBottom ? (
        <ArrowButton
          containerRef={containerRef}
          btnContainerClassName={btnContainerClassName}
          scrollValueRef={scrollValueRef}
          numOfCards={numOfCards}
          perSlide={perSlide}
          infinite={infinite}
        />
      ) : null}
    </>
  );
};

export default ScrollableSection;
